import {
  FormOperationsProvider,
  KnowYourCustomer,
  kycSchemaResolver,
} from '@huspy/forge/features';
import { KYCApplicant, KYCCountry } from '@huspy/forge/types';

import React, {
  useContext, useEffect, useState
} from 'react';
import { IS_SPAIN_ENV } from '@modules/core/utils';
import {
  Box, Button, Flex
} from '@huspy/briks-web';
import { scrollToError } from '@shared/utils';
import { CaseCreationContext } from '@modules/opportunities/presentation/pages/CaseCreation/';
import { useTranslation } from 'react-i18next';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';
import useClientOpportunity from '@modules/opportunities/entities/opportunity/query/useClientOpportunity';
import { useCreateVaultStorage } from '@modules/vault/hooks/mutations/useCreateVaultStorage';
import { useSubmitVault } from '@modules/vault/hooks/mutations/submitVault';
import { useDisclosure } from '@mantine/hooks';
import { UseFormReturnType } from '@mantine/form';
import { OPPORTUNITY_STATUS } from '@modules/opportunities/entities/opportunity/const';
import { useVaultFormErrorHandler } from '@modules/opportunities/presentation/hooks/useHandleErrors';
import { useLocation } from '@tanstack/react-router';
import {
  useVaultFormContext,
  useVaultsForm,
  VaultFormProvider,
  VaultFormValues,
} from './form-context';
import { VaultFormHeader } from './VaultFormHeader';
import { FormSection } from './FormSection';
import { Footer } from './Footer';
import { ConfirmationSlido } from './ConfirmationSlido';
import { getApplicantFullName, getInitialValues } from './utils';

type VaultFormType = UseFormReturnType<
VaultFormValues<KYCCountry>,
(values: VaultFormValues<KYCCountry>) => VaultFormValues<KYCCountry>
>;

interface VaultFormProps {
  applicantsKYC: KYCApplicant[];
  opportunityId: string;
  banks: {
    label: string;
    value: string;
  }[];
  isClientNotLoggedIn: boolean;
  isVaultSubmitted: boolean;
  isVaultDisabled: boolean;
}

const useVaultFormSubmission = (
  form: VaultFormType,
  opportunityId: string,
  clientExternalId: string,
  isClientNotLoggedIn: boolean
) => {
  const {
    mutateAsync: submitVault,
    isPending: isSubmitVaultLoading,
    error,
  } = useSubmitVault(opportunityId, clientExternalId);
  const {
    mutateAsync: createVaultStorage,
    isPending: isVaultCreationLoading,
  } = useCreateVaultStorage(opportunityId);

  const saveData = async () => {
    const data = form.getValues();
    await submitVault(data);
    form.resetDirty();
  };

  const handleSubmit = async (onSuccess = () => {}) => {
    try {
      if (isClientNotLoggedIn) {
        await createVaultStorage();
      }
      await saveData();
      onSuccess();
    } catch (error_) {
      console.error(error_);
    }
  };

  return {
    handleSubmit,
    isLoading: isSubmitVaultLoading || isVaultCreationLoading,
    error,
  };
};

const VaultForm: React.FC<VaultFormProps> = ({
  applicantsKYC,
  opportunityId,
  banks,
  isClientNotLoggedIn,
  isVaultSubmitted,
  isVaultDisabled,
}) => {
  const { t } = useTranslation();
  const [formHasErrors, setFormHasErrors] = useState(false);

  const [isOpen, { close, open }] = useDisclosure(false);
  const { goToNextState } = useContext(CaseCreationContext);
  const { data: opportunityData } = useClientOpportunity(opportunityId);
  const clientExternalId = opportunityData?.opportunity.client_external_id;

  const initialValues = getInitialValues(applicantsKYC);
  const form = useVaultsForm({
    mode: 'uncontrolled',
    initialValues,
    validate: kycSchemaResolver(IS_SPAIN_ENV),
  });

  const { hash } = useLocation();

  const {
    handleSubmit,
    isLoading,
    error: vaultErrors,
  } = useVaultFormSubmission(
    form,
    opportunityId,
    clientExternalId!,
    isClientNotLoggedIn
  );

  useEffect(() => {
    trackAmplitudeEvent(USER_EVENTS.VAULT.CASE_CLIENT_REQUESTED_INFO_VIEWED, {
      case_id: opportunityId,
      client_id: clientExternalId,
    });
  }, [opportunityId, clientExternalId]);

  const isNotDraft = opportunityData?.opportunity.status !== OPPORTUNITY_STATUS.draft;
  const isAllFieldDisabled = (() => {
    if (isNotDraft) return true;
    if (isVaultDisabled) return true;
    if (formHasErrors) return false;
    if (isClientNotLoggedIn) return true;
    return !isVaultSubmitted;
  })();

  const validateAndScroll = (
    onSuccess: () => void,
    onError: () => void = () => {}
  ) => {
    const { errors, hasErrors } = form.validate();
    if (hasErrors) {
      onError();
      scrollToError(errors);
      return;
    }
    onSuccess();
  };

  const showEditFooter = !isNotDraft && !isVaultDisabled && (isAllFieldDisabled || formHasErrors);

  const trackSubmitButtonClicked = () => {
    trackAmplitudeEvent(
      USER_EVENTS.VAULT.CASE_CLIENT_REQUESTED_INFO_SUBMIT_CLICKED,
      {
        case_id: opportunityId,
        client_id: clientExternalId,
      }
    );
  };

  const trackCancelSubmit = () => {
    trackAmplitudeEvent(
      USER_EVENTS.VAULT.CASE_CLIENT_REQUESTED_INFO_SUBMIT_CANCELED,
      {
        case_id: opportunityId,
        client_id: clientExternalId,
      }
    );
  };

  useVaultFormErrorHandler({
    vaultErrors,
    form,
    callback: () => setFormHasErrors(!isVaultSubmitted),
  });

  useEffect(() => {
    if (hash === 'validate') {
      const { errors, hasErrors } = form.validate();
      if (hasErrors) {
        scrollToError(errors);
        setFormHasErrors(true);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash]);

  return (
    <VaultFormProvider form={ form }>
      <FormOperationsProvider
        operations={ {
          useVaultFormContext,
          banks,
          isAllFieldDisabled,
        } }
      >
        <form onSubmit={ (e) => e.preventDefault() }>
          <Box position='relative'>
            <VaultFormHeader
              isClientNotLoggedIn={ isClientNotLoggedIn }
              isVaultSubmitted={ isVaultSubmitted }
              isVaultDisabled={ isVaultDisabled }
            />
            {applicantsKYC.map((applicant, idx) => (
              <Box key={ applicant.applicant_id } position='relative' my='8'>
                <FormSection applicantName={ getApplicantFullName(applicant) }>
                  <KnowYourCustomer
                    key={ applicant.applicant_id }
                    formKey={ `applicants.${idx}` }
                    applicant={ applicant }
                    isSpainBusiness={ IS_SPAIN_ENV }
                  />
                </FormSection>
              </Box>
            ))}
            {showEditFooter && (
              <Footer
                handleClick={ () => {
                  trackSubmitButtonClicked();
                  validateAndScroll(open, () => setFormHasErrors(true));
                } }
                isLoading={ isLoading }
                formHasErrors={ formHasErrors }
                isAllFieldDisabled={ isAllFieldDisabled }
              />
            )}
          </Box>
          <Flex
            data-testid='submit-vault-form-footer'
            align='end'
            justify='space-between'
            display={ showEditFooter ? 'none' : 'flex' }
          >
            <Button
              display={ isNotDraft ? 'none' : 'block' }
              data-test='save-btn'
              type='button'
              variant='outline'
              onClick={ () => validateAndScroll(handleSubmit) }
              loading={ isLoading }
            >
              {t('kyc.saveBtn')}
            </Button>
            <Button
              marginLeft='auto'
              data-test='proceed-remaining-fields-btn'
              type='submit'
              loading={ isLoading }
              variant={ isLoading ? 'secondary' : 'primary' }
              onClick={ () => {
                if (isNotDraft) {
                  goToNextState();
                } else {
                  validateAndScroll(() => handleSubmit(goToNextState));
                }
              } }
            >
              {t('kyc.proceedBtn')}
            </Button>
          </Flex>
          <ConfirmationSlido
            opened={ isOpen }
            close={ () => {
              trackCancelSubmit();
              close();
            } }
            save={ () => handleSubmit(() => {
              setFormHasErrors(false);
              close();
            }) }
            isAllFieldDisabled={ isAllFieldDisabled }
          />
        </form>
      </FormOperationsProvider>
    </VaultFormProvider>
  );
};

export default VaultForm;
